import {defineStore} from 'pinia';
import {ref, computed} from 'vue';
import {useApiClient} from "@/hooks/api-hook.ts";
import {User, UserCreateRequest} from "@/api/client/models/index.ts";

export const useCharterUsersStore = defineStore('charter-users', () => {
  const api = useApiClient()

  // State
  const users = ref<User[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const usersCount = computed(() => users.value.length);
  const hasUsers = computed(() => users.value.length > 0);
  const isLoading = computed(() => loading.value);
  const hasError = computed(() => error.value !== null);

  // Actions
  async function fetchUsers(isActive: boolean = true) {
    loading.value = true;
    error.value = null;

    try {
      users.value = await api.user.get({queryParameters: {isActive: isActive}})
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch users';
      console.error('Error fetching users:', err);
    } finally {
      loading.value = false;
    }
  }

  function clearUsers() {
    users.value = [];
    error.value = null;
  }

  function clearError() {
    error.value = null;
  }

  async function addUser(userCreateRequest: UserCreateRequest) {
    loading.value = true;
    error.value = null;

    try {
      const newUser = await api.user.post(userCreateRequest);
      users.value.push(newUser);
      return newUser;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to add user';
      console.error('Error adding user:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    // State
    users,
    loading,
    error,

    // Getters
    usersCount,
    hasUsers,
    isLoading,
    hasError,

    // Actions
    fetchUsers,
    clearUsers,
    clearError,
    addUser
  };
});
